import { AfterViewInit, Component, ElementRef, HostBinding, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ThemePalette } from '../../model/palette/palette.model';
import { Product } from '../../model/product/product.model';
import { ThemeService } from '../../service/theme/theme.service';
import { DarkLightSettings } from '../../data/theme/theme.data';
import { PopoverShape } from '../theme-expander/popover-shape';

/**
 * Full-width detail row for the palettes page grid. Opens between the clicked
 * poster card's row and the next row and shows the home's palettes box
 * (app-palette-showcase) for this one palette, as a popover pointing at the
 * card: the theme expander's shape and stylesheet (owner 2026-10-15). The box
 * keeps its own state (app on show, cart, copy a hex).
 */
@Component( {
  selector: 'app-palette-expander',
  templateUrl: './palette-expander.component.html',
  styleUrls: [ '../theme-expander/theme-expander.component.scss' ],
} )
export class PaletteExpanderComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() product: Product = undefined;
  @Input() category: string;
  @Input() palette: ThemePalette | null = null;
  // Palettes-page platform filter (an app's name, or undefined): the box opens on that app, as the theme
  // expander opens on the filtered platform.
  @Input() selectedPlatformFilter?: string;
  /** the box in the page itself, no popover (the palette page's top box, owner 2026-10-15) */
  @Input() @HostBinding( 'class.inline' ) inline = false;

  /** the page's band behind the box (the tool pages' ground, owner 2026-10-15) in this theme's first colour */
  @HostBinding( 'style.--box-band-color' )
  get bandColor(): string | null {
    return this.inline ? this.product?.designTokens?.primaryColor || null : null;
  }

  /** the box's one palette */
  entry: { product: Product; category: string; palette: ThemePalette | null; app?: string } | null = null;

  // Site dark/light MODE only - never the applied product skin: the box
  // takes it as the site's mode (siteDark).
  darkModeActive = false;

  /** The box as a popover pointing at the clicked card. */
  private readonly shape: PopoverShape;

  constructor( host: ElementRef<HTMLElement>, zone: NgZone, private themeService: ThemeService ) {
    this.shape = new PopoverShape( host, zone );
    this.themeService.darkLightSetting.subscribe( darkLightSetting => {
      this.darkModeActive = darkLightSetting === DarkLightSettings.dark
        || ( darkLightSetting !== DarkLightSettings.light
             && this.themeService.selectDarkLightByCSS( false, true ) );
      if ( DarkLightSettings.auto === darkLightSetting ) {
        window.matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', () => {
          this.darkModeActive = this.themeService.selectDarkLightByCSS( false, true );
        } );
      }
    } );
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( !this.product || !( changes.product || changes.palette || changes.category || changes.selectedPlatformFilter ) ) {
      return;
    }
    this.entry = { product: this.product, category: this.category, palette: this.palette, app: this.selectedPlatformFilter };
    // another card of the same row moves the arrow
    if ( !this.inline ) {
      this.shape.schedule();
    }
  }

  ngAfterViewInit(): void {
    if ( !this.inline ) {
      this.shape.attach();
    }
  }

  ngOnDestroy(): void {
    this.shape.destroy();
  }
}
